import { useEffect, useState, useCallback, useMemo, memo } from "react";
import {
  Form,
  Input,
  Select,
  Button,
  Row,
  Col,
  Switch,
} from "antd";
import { useSelector, useDispatch } from "react-redux";
import {
  setEntity,
} from "../../../features/cartransferSlice";
import { toast } from "../../../utils/toasts";
import {
  parseFieldsToState,
  parseStateToFields,
} from "../../../utils/parseFormfields";
import {
  FilePdfOutlined,
  FileWordOutlined,
  SyncOutlined,
  LockOutlined,
  UnlockOutlined,
  FileTextOutlined,
  CheckCircleOutlined,
  DollarOutlined,
  CarOutlined,
  ToolOutlined,
  CaretDownOutlined,
  CaretRightOutlined,
} from "@ant-design/icons";
import { useDebouncedCallback } from "use-debounce";

const { Option } = Select;

// ── Design tokens ─────────────────────────────────────────────
const ACCENT = "#1d4ed8";
const LIGHT  = "#eff6ff";
const BORDER = "#bfdbfe";

const PanelHeader = ({ icon, title, hasDoc }) => (
  <div
    style={{
      display: "flex",
      alignItems: "center",
      gap: 10,
      padding: "10px 16px",
      background: LIGHT,
      border: `1px solid ${BORDER}`,
      borderRadius: 10,
      borderLeft: `4px solid ${ACCENT}`,
      marginBottom: 0,
    }}
  >
    <span style={{ color: ACCENT, fontSize: 16 }}>{icon}</span>
    <span style={{ fontWeight: 700, fontSize: 14, color: ACCENT, flex: 1 }}>{title}</span>
    {hasDoc && <CheckCircleOutlined style={{ color: "#22c55e", fontSize: 15 }} />}
  </div>
);

const FieldSection = ({ icon, label }) => (
  <div
    style={{
      display: "flex",
      alignItems: "center",
      gap: 6,
      fontSize: 10,
      fontWeight: 700,
      letterSpacing: 1.2,
      textTransform: "uppercase",
      color: "#b0b0b0",
      marginBottom: 10,
      marginTop: 4,
      paddingBottom: 6,
      borderBottom: "1px solid #f0f0f0",
    }}
  >
    {icon && <span style={{ fontSize: 11 }}>{icon}</span>}
    {label}
  </div>
);

const fi = { marginBottom: 10 };

const CarStep = memo(({ label, handleFinish, buttons, loading }) => {
  const dispatch = useDispatch();

  const contract = useSelector(state => state.cartransfer.contract);
  const background = useSelector(state => state.cartransfer.background);
  const permission = useSelector(state => state.cartransfer.permission);
  const repertory = useSelector(state => state.cartransfer.repertory);
  const repertoryYear = useSelector(state => state.cartransfer.repertoryYear);

  const entity = "contract";
  const [form] = Form.useForm();
  const gases = useMemo(() => ["gasolina", "diesel", "electrico", "hibrido"], []);
  const [enabledInputs, setenabledInputs] = useState({});
  const [techExpanded, setTechExpanded] = useState(false);
  const today = useMemo(() => new Date().toISOString().split("T")[0], []);

  const hasContract = !!(buttons?.pdf || buttons?.word);

  const toNumeric = useCallback((val) => {
    if (val === null || val === undefined) return NaN;
    const onlyDigits = String(val).replace(/\D+/g, "");
    if (!onlyDigits) return NaN;
    return Number(onlyDigits);
  }, []);

  const handleEnable = useCallback((type) => {
    const iValue = enabledInputs[type];
    const click = () => setenabledInputs(prev => ({ ...prev, [type]: !iValue }));
    const Component = iValue ? LockOutlined : UnlockOutlined;
    return <Component onClick={click} />;
  }, [enabledInputs]);

  useEffect(() => {
    const sourceData = (contract && Object.keys(contract).length > 0) ? contract : (background || {});
    if (sourceData && Object.keys(sourceData).length > 0) {
      const formValues = {
        ...sourceData,
        ...(Object.keys(background || {}).length > 0 ? background : {}),
        gas: sourceData.gas || background?.gas || permission?.gas,
        appraisal: sourceData.appraisal || contract?.permission?.appraisal || background?.appraisal || permission?.appraisal,
        paymentMethod: sourceData.paymentMethod || "EFECTIVO",
        repertory: repertory,
        repertoryYear: repertoryYear,
      };
      setTimeout(() => { form.setFieldsValue(formValues); }, 100);
    }
  }, [background, permission, contract, repertory, repertoryYear, form]);

  useEffect(() => {
    if (((background && Object.keys(background).length > 0) || (permission && Object.keys(permission).length > 0)) &&
        (!contract || Object.keys(contract).length === 0)) {
      const initialContractData = {
        ...(background || {}),
        gas: background?.gas || permission?.gas,
        appraisal: background?.appraisal || contract?.permission?.appraisal || permission?.appraisal || 1,
        paymentMethod: "EFECTIVO",
        date: today,
        signDate: today,
        payDate: today,
        price: parseInt(background?.appraisal || contract?.permission?.appraisal || permission?.appraisal || 1),
        tax: Math.round(parseInt(background?.appraisal || contract?.permission?.appraisal || permission?.appraisal || 1) * 0.015),
        exploits: false,
      };
      dispatch(setEntity({ entity: "contract", data: initialContractData }));
    }
  }, [background, contract, dispatch, permission, today]);

  const debouncedUpdateFields = useDebouncedCallback(
    (allFields) => {
      dispatch(setEntity({ entity, data: parseFieldsToState(allFields) }));
    },
    500
  );

  const debouncedUpdateTax = useDebouncedCallback(
    (priceValue) => {
      dispatch(setEntity({ entity, data: { tax: Math.round(priceValue * 0.015) } }));
    },
    500
  );

  const handleGas = useCallback((permission, background) => {
    const currentGas = background.gas || permission.gas;
    if (!currentGas) return;
    if (gases.includes(currentGas.toLowerCase())) {
      return { gas: currentGas };
    }
  }, [gases]);

  useEffect(() => {
    if ((!contract || Object.keys(contract).length === 0) &&
        ((background && Object.keys(background).length > 0) ||
         (permission && Object.keys(permission).length > 0))) {
      const appraisalValue = parseInt(
        contract?.appraisal || contract?.permission?.appraisal || background?.appraisal || permission?.appraisal || 1
      );
      const newData = {
        ...(background || {}),
        ...handleGas(permission, background || {}),
        appraisal: appraisalValue,
        siicode: contract?.siicode || contract?.permission?.siicode || permission?.siicode,
        paymentMethod: "EFECTIVO",
        date: today,
        signDate: today,
        payDate: today,
        price: (contract?.price || appraisalValue),
        tax: Math.round(parseInt(appraisalValue) * 0.015),
      };
      dispatch(setEntity({ entity, data: newData }));
    }
  }, [background, permission, contract, today, handleGas, dispatch, entity]);

  useEffect(() => {
    if (contract && (!contract.paymentMethod || contract.paymentMethod.trim() === "")) {
      dispatch(setEntity({ entity, data: { paymentMethod: "EFECTIVO" } }));
    }
  }, [contract, dispatch, entity]);

  useEffect(() => {
    const appraisalCandidate =
      toNumeric(contract?.appraisal) ||
      toNumeric(contract?.permission?.appraisal) ||
      toNumeric(background?.appraisal) ||
      toNumeric(permission?.appraisal) ||
      0;

    const currentPrice = toNumeric(contract?.price);

    if (isNaN(currentPrice) || currentPrice <= 0 || currentPrice === 1) {
      const newPrice = appraisalCandidate > 0 ? appraisalCandidate : 1;
      form.setFieldsValue({ price: newPrice });
      dispatch(setEntity({ entity, data: { price: newPrice, tax: Math.round(newPrice * 0.015) } }));
    }
  }, [contract?.appraisal, contract?.permission?.appraisal, background?.appraisal, permission?.appraisal, contract?.price, dispatch, form, toNumeric]);

  const onFieldsChange = useCallback((current, allFields) => {
    debouncedUpdateFields(allFields);

    if (current[0] && current[0].name.includes("paymentMethod")) {
      dispatch(setEntity({ entity, data: { paymentMethod: current[0].value } }));
    }

    if (current[0] && current[0].name.includes("price")) {
      const raw = current[0].value;
      const priceValue = parseInt(raw);
      const appraisalValue = parseInt(
        form.getFieldValue("appraisal") ??
        contract?.appraisal ??
        contract?.permission?.appraisal ??
        permission?.appraisal
      );
      if (!raw || isNaN(priceValue) || priceValue <= 0) {
        const newPrice = (!isNaN(appraisalValue) && appraisalValue > 0) ? appraisalValue : 1;
        form.setFieldsValue({ price: newPrice });
        dispatch(setEntity({ entity, data: { price: newPrice, tax: Math.round(newPrice * 0.015) } }));
      } else {
        if (!isNaN(appraisalValue) && appraisalValue > 0 && priceValue < appraisalValue) {
          toast("warning", `⚠️ El precio ($${priceValue.toLocaleString('es-CL')}) debe ser mayor a la tasación ($${appraisalValue.toLocaleString('es-CL')})`);
        }
        debouncedUpdateTax(priceValue);
      }
    }

    if (current[0] && current[0].name.includes("appraisal")) {
      const appraisalValue = parseInt(current[0].value);
      const currentPrice = form.getFieldValue("price");
      const currentPriceNum = parseInt(currentPrice);
      const shouldSetPrice = !currentPrice || isNaN(currentPriceNum) || currentPriceNum <= 0;
      if (!shouldSetPrice && !isNaN(appraisalValue) && appraisalValue > 0 && currentPriceNum < appraisalValue) {
        toast("warning", `⚠️ El precio actual ($${currentPriceNum.toLocaleString('es-CL')}) debe ser mayor a la tasación ($${appraisalValue.toLocaleString('es-CL')})`);
      }
      if (shouldSetPrice) {
        const newPrice = !isNaN(appraisalValue) && appraisalValue > 0 ? appraisalValue : 1;
        form.setFieldsValue({ price: newPrice });
        dispatch(setEntity({ entity, data: { price: newPrice, tax: Math.round(newPrice * 0.015) } }));
      }
    }

    if (current[0] && current[0].name.includes("exploits")) {
      dispatch(setEntity({
        entity,
        data: {
          signDate: current[0].value ? null : contract.date,
          payDate: current[0].value ? null : contract.date,
        },
      }));
    }
  }, [debouncedUpdateFields, debouncedUpdateTax, dispatch, entity, form, contract, permission]);

  return (
    <>
      {/* ── Panel: Generar contrato ───────────────────────── */}
      <PanelHeader icon={<FilePdfOutlined />} title="Generar contrato" hasDoc={hasContract} />
      <div
        style={{
          border: `1px solid ${ACCENT}30`,
          borderTop: "none",
          borderRadius: "0 0 10px 10px",
          padding: "14px 16px 12px",
          background: "#fff",
          marginBottom: 16,
        }}
      >
        <Row gutter={[8, 8]} align="middle">
          <Col>
            <Button
              type={repertory ? "primary" : "default"}
              onClick={handleFinish}
              disabled={!repertory}
              loading={loading}
              style={repertory ? { background: ACCENT, borderColor: ACCENT } : {}}
            >
              {repertory ? "Generar archivos" : "Sin repertorio"}
            </Button>
          </Col>
          {buttons?.word && (
            <Col>
              <Button
                icon={<SyncOutlined />}
                onClick={handleFinish}
                loading={loading}
                style={{ color: ACCENT, borderColor: ACCENT }}
              />
            </Col>
          )}
          {buttons?.pdf && (
            <Col>
              <Button icon={<FilePdfOutlined />} type="link" href={buttons.pdf} style={{ color: "#ef4444", paddingLeft: 4 }}>
                PDF
              </Button>
            </Col>
          )}
          {buttons?.word && (
            <Col>
              <Button icon={<FileWordOutlined />} type="link" href={buttons.word} style={{ color: "#2563eb", paddingLeft: 4 }}>
                Word
              </Button>
            </Col>
          )}
          {buttons?.pdfCopy && (
            <Col>
              <Button icon={<FilePdfOutlined />} type="link" href={buttons.pdfCopy} style={{ color: "#ef4444", paddingLeft: 4 }}>
                Copia SII
              </Button>
            </Col>
          )}
        </Row>
      </div>

      {/* ── Panel: Confirmar datos ────────────────────────── */}
      <PanelHeader icon={<FileTextOutlined />} title={label || "Confirmar datos"} />
      <div
        style={{
          border: `1px solid ${ACCENT}30`,
          borderTop: "none",
          borderRadius: "0 0 10px 10px",
          padding: "16px 16px 10px",
          background: "#fff",
        }}
      >
        <Form
          form={form}
          initialValues={
            contract && Object.keys(contract).length > 0
              ? { ...contract, repertory, repertoryYear }
              : {
                  ...background,
                  gas: background?.gas || permission?.gas,
                  appraisal: background?.appraisal || permission?.appraisal,
                  paymentMethod: "EFECTIVO",
                  repertory,
                }
          }
          name={entity}
          onFieldsChange={onFieldsChange}
          fields={parseStateToFields(contract)}
          layout="vertical"
        >
          {/* ── Precio y pago ─────────────────────────────── */}
          <FieldSection icon={<DollarOutlined />} label="Precio y pago" />
          <Row gutter={12}>
            <Col span={14}>
              <Form.Item name="price" label="Precio de venta" style={fi}>
                <Input
                  type="number"
                  size="middle"
                  addonAfter={handleEnable("price")}
                  disabled={!enabledInputs.price}
                />
              </Form.Item>
            </Col>
            <Col span={10}>
              <Form.Item name="paymentMethod" label="Método de pago" style={fi}>
                <Select size="middle">
                  <Option value="EFECTIVO">Efectivo</Option>
                  <Option value="VALE VISTA">Vale Vista</Option>
                  <Option value="CHEQUE">Cheque</Option>
                  <Option value="OTRO">Otro</Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>

          {/* ── Vehículo ──────────────────────────────────── */}
          <FieldSection icon={<CarOutlined />} label="Vehículo" />
          <Row gutter={12}>
            <Col span={8}>
              <Form.Item name="inscription" label="Patente" style={fi}>
                <Input
                  size="middle"
                  addonAfter={handleEnable("inscription")}
                  disabled={!enabledInputs.inscription}
                />
              </Form.Item>
            </Col>
            <Col span={10}>
              <Form.Item name="type" label="Tipo" style={fi}>
                <Input
                  size="middle"
                  addonAfter={handleEnable("type")}
                  disabled={!enabledInputs.type}
                />
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item name="year" label="Año" style={fi}>
                <Input
                  size="middle"
                  addonAfter={handleEnable("year")}
                  disabled={!enabledInputs.year}
                />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={12} align="middle">
            <Col span={14}>
              <Form.Item name="gas" label="Combustible" style={fi}>
                <Select size="middle">
                  <Option value="DIESEL">Diesel</Option>
                  <Option value="GASOLINA">Gasolina</Option>
                  <Option value="GAS">Gas</Option>
                  <Option value="ELECTRICO">Eléctrico</Option>
                </Select>
              </Form.Item>
            </Col>
            <Col span={10}>
              <Form.Item name="exploits" label="Explota el giro" valuePropName="checked" style={fi}>
                <Switch checkedChildren="Sí" unCheckedChildren="No" />
              </Form.Item>
            </Col>
          </Row>

          {/* ── Datos técnicos (colapsable) ────────────────── */}
          <div
            onClick={() => setTechExpanded((v) => !v)}
            style={{
              display: "flex",
              alignItems: "center",
              gap: 6,
              cursor: "pointer",
              padding: "7px 10px",
              borderRadius: techExpanded ? "6px 6px 0 0" : 6,
              background: techExpanded ? LIGHT : "#fafafa",
              border: `1px solid ${techExpanded ? BORDER : "#e8e8e8"}`,
              marginBottom: techExpanded ? 0 : 4,
              userSelect: "none",
            }}
          >
            <ToolOutlined style={{ color: techExpanded ? ACCENT : "#999", fontSize: 12 }} />
            <span style={{ fontSize: 11, fontWeight: 700, letterSpacing: 1, textTransform: "uppercase", color: techExpanded ? ACCENT : "#999" }}>
              Datos técnicos
            </span>
            <span style={{ marginLeft: "auto", color: "#aaa", fontSize: 10 }}>
              {techExpanded ? <CaretDownOutlined /> : <CaretRightOutlined />}
            </span>
          </div>

          {techExpanded && (
            <div
              style={{
                border: `1px solid ${BORDER}`,
                borderTop: "none",
                borderRadius: "0 0 6px 6px",
                padding: "12px 12px 4px",
                background: "#f8faff",
                marginBottom: 4,
              }}
            >
              <Row gutter={12}>
                <Col span={12}>
                  <Form.Item name="date" label="Fecha" style={fi}>
                    <Input
                      type="date"
                      size="middle"
                      addonAfter={handleEnable("date")}
                      disabled={!enabledInputs.date}
                    />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item name="appraisal" label="Tasación" style={fi}>
                    <Input
                      size="middle"
                      addonAfter={handleEnable("appraisal")}
                      disabled={!enabledInputs.appraisal}
                    />
                  </Form.Item>
                </Col>
              </Row>
              <Row gutter={12}>
                <Col span={12}>
                  <Form.Item name="siicode" label="Código SII" style={fi}>
                    <Input
                      size="middle"
                      addonAfter={handleEnable("siicode")}
                      disabled={!enabledInputs.siicode}
                    />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item name="tax" label="Impuesto (1.5%)" style={fi}>
                    <Input size="middle" type="number" disabled />
                  </Form.Item>
                </Col>
              </Row>
              <Row gutter={12}>
                <Col span={12}>
                  <Form.Item name="make" label="Marca" style={fi}>
                    <Input
                      size="middle"
                      addonAfter={handleEnable("make")}
                      disabled={!enabledInputs.make}
                    />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item name="model" label="Modelo" style={fi}>
                    <Input
                      size="middle"
                      addonAfter={handleEnable("model")}
                      disabled={!enabledInputs.model}
                    />
                  </Form.Item>
                </Col>
              </Row>
              <Row gutter={12}>
                <Col span={8}>
                  <Form.Item name="motor" label="N° motor" style={fi}>
                    <Input
                      size="middle"
                      addonAfter={handleEnable("motor")}
                      disabled={!enabledInputs.motor}
                    />
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item name="chasis" label="N° chasis" style={fi}>
                    <Input
                      size="middle"
                      addonAfter={handleEnable("chasis")}
                      disabled={!enabledInputs.chasis}
                    />
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item name="vin" label="VIN" style={fi}>
                    <Input
                      size="middle"
                      addonAfter={handleEnable("vin")}
                      disabled={!enabledInputs.vin}
                    />
                  </Form.Item>
                </Col>
              </Row>
              <Row gutter={12}>
                <Col span={12}>
                  <Form.Item name="serial" label="N° serie" style={fi}>
                    <Input
                      size="middle"
                      addonAfter={handleEnable("serial")}
                      disabled={!enabledInputs.serial}
                    />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item name="color" label="Color" style={fi}>
                    <Input
                      size="middle"
                      addonAfter={handleEnable("color")}
                      disabled={!enabledInputs.color}
                    />
                  </Form.Item>
                </Col>
              </Row>
            </div>
          )}
        </Form>
      </div>
    </>
  );
});

export default CarStep;
